import { useEffect } from 'react';
import treatmentReportsService from '../../../services/treatmentReportsService';
import { getFranchiseeId } from '../services/reportHelpers';

export const useReportData = (userProfile, reportState, filters, notifications) => {
  const { 
    setReports, 
    setIsLoading, 
    setTechnicians, 
    setPagination,
    pagination 
  } = reportState;
  
  const { 
    debouncedSearchTerm, 
    statusFilter, 
    technicianFilter 
  } = filters;
  
  const { showNotification } = notifications;

  // Load treatment reports
  const loadReports = async (page = 1, search = '', status = '', technician = '') => {
    const franchiseeId = getFranchiseeId(userProfile);
    if (!franchiseeId) {
      setReports([]);
      setPagination({ page: 1, limit: 50, total: 0, pages: 0 });
      return;
    }

    setIsLoading(true);
    try {
      const response = await treatmentReportsService.getTreatmentReports(franchiseeId, {
        page,
        limit: 50,
        search: search || undefined,
        status: status || undefined,
        technician: technician || undefined,
        sortBy: 'service_date',
        sortOrder: 'DESC'
      });

      if (response.reports) {
        setReports(response.reports);
        setPagination(response.pagination || { page, limit: 50, total: 0, pages: 0 });
      }
    } catch (error) {
      console.error('Error loading treatment reports:', error);
      showNotification('Failed to load treatment reports', 'error');
      setReports([]);
      setPagination({ page: 1, limit: 50, total: 0, pages: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  // Load technicians for filter dropdown
  const loadTechnicians = async () => {
    const franchiseeId = getFranchiseeId(userProfile);
    if (!franchiseeId) return;

    try {
      const response = await treatmentReportsService.getTechnicians(franchiseeId);
      if (response.technicians) {
        setTechnicians(response.technicians);
      }
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  // Initial load when userProfile changes
  useEffect(() => {
    loadReports();
    loadTechnicians();
  }, [userProfile]);

  // Reload when filters change
  useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      loadReports(1, debouncedSearchTerm, statusFilter, technicianFilter);
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [debouncedSearchTerm, statusFilter, technicianFilter]);

  // Handle pagination changes
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      loadReports(newPage, debouncedSearchTerm, statusFilter, technicianFilter);
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  // Reload current page
  const reloadCurrentPage = () => {
    loadReports(pagination.page, debouncedSearchTerm, statusFilter, technicianFilter);
  };

  return {
    loadReports,
    loadTechnicians,
    handlePageChange,
    reloadCurrentPage
  };
};